/**
 * O `fetch` tipado — **o unico lugar do produto onde uma requisicao HTTP nasce** (ADR-0001:
 * *"fica proibido no codigo do cliente [...] fetch fora de `@farmacia/api-client`"*).
 *
 * Tudo que toda requisicao precisa fazer e feito aqui, uma vez:
 *
 * | Obrigacao | Onde esta escrita | Como sai daqui |
 * | --------- | ----------------- | -------------- |
 * | Sessao em cookie opaco, sem token | ADR-0009, `API §5.1` | `credentials: 'include'` e **nenhum** `Authorization` |
 * | CSRF em toda mutacao | `API §5.1`, `CONTRACTS §5.7` | `X-CSRF-Token` lido do cookie `__Host-csrf` |
 * | `X-Client` obrigatorio | `API §1.2` | montado da configuracao |
 * | `Idempotency-Key` nas escritas nao-idempotentes | `API §3.1` | do `IdempotencyKeeper`, reusada na retentativa |
 * | `If-Match` nas rotas com concorrencia | `API §4` | do `ResourceVersionStore` |
 * | `429` com `Retry-After` e jitter | `API §9` | `rate-limit.ts` |
 * | Erro RFC 9457 -> tipo da aplicacao | `API §2.1` | `errors.ts` |
 *
 * ## O que NAO existe aqui, e e proposital
 *
 * Nao ha parametro de headers arbitrarios. Se a tela pudesse passar `headers`, ela poderia
 * passar `Authorization` — e a proibicao do ADR-0009 viraria convencao em vez de estrutura.
 * O conjunto de cabecalhos e fechado e montado por `buildHeaders`, que tem um teste dedicado.
 */
import {
  ACCEPT_LANGUAGE_HEADER_NAME,
  CLIENT_HEADER_NAME,
  CartVersionConflictMetaSchema,
  CSRF_HEADER_NAME,
  ETAG_HEADER_NAME,
  IDEMPOTENCY_KEY_HEADER_NAME,
  IDEMPOTENT_REPLAYED_HEADER_NAME,
  IF_MATCH_HEADER_NAME,
  IF_NONE_MATCH_HEADER_NAME,
  JSON_MEDIA_TYPE,
  PROBLEM_JSON_MEDIA_TYPE,
  RATE_LIMIT_HEADER_NAME,
  REQUEST_ID_HEADER_NAME,
  RETRY_AFTER_HEADER_NAME,
} from '@farmacia/contracts';
import type { z } from 'zod';

import type { ResolvedApiClientConfig } from './config.js';
import {
  ApiError,
  ApiTransportError,
  CartVersionConflictError,
  parseProblemDetails,
  type ApiErrorInput,
} from './errors.js';
import { defaultIdempotencyScope } from './idempotency.js';
import type { IdempotencyKeeper } from './idempotency.js';
import {
  jitteredDelayMs,
  parseRateLimit,
  parseRetryAfterSeconds,
  retryBudget,
  type RateLimitSnapshot,
} from './rate-limit.js';

export type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** `GET`, `HEAD` e `OPTIONS` nao mutam: sem CSRF, sem idempotencia (RFC 9110, `csrf.ts` da API). */
const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * `429` sem `Retry-After` nao deveria acontecer (`API §9` sempre o manda). Se acontecer, um
 * segundo e o piso — o documento proibe **inventar** o intervalo, nao ter um chao quando o
 * servidor omite o dado.
 */
export const DEFAULT_RETRY_AFTER_SECONDS = 1;

/**
 * `5xx` repete **uma vez**. E o que o catalogo escreve na linha `INTERNAL_ERROR`
 * (`acaoSugerida: 'RETRY' // 1 vez`, `API §2.2`).
 */
export const RETRY_ATTEMPTS_SERVER_ERROR = 1;

/**
 * Sessao perdida: os codigos que disparam `clearUserScopedStorage` (`PWA-034`).
 *
 * **DUVIDA:** `PWA §7.5` nomeia tres gatilhos — `401 SESSION_REVOKED`, `403 ACCOUNT_REVOKED` e
 * o `LOGGED_OUT` do `BroadcastChannel`. `ACCOUNT_REVOKED` **nao existe** no catalogo fechado de
 * 106 codigos (`API §2.2`), e o catalogo precede o `PWA.md` na cadeia de precedencia. Ficam os
 * dois que existem; o terceiro e da aplicacao, nao do cliente HTTP. Reportado.
 */
const SESSION_LOST_CODES: ReadonlySet<string> = new Set(['UNAUTHENTICATED', 'SESSION_REVOKED']);

export type QueryValue = string | number | boolean | readonly (string | number)[] | undefined;

export interface ApiRequestSpec<TResponse> {
  readonly method: HttpMethod;
  /** Caminho ja resolvido, comecando em `/v1/` — `'/v1/cart/items/01J...'`. */
  readonly path: string;
  readonly query?: Readonly<Record<string, QueryValue>>;
  /** Corpo JSON. `undefined` nao emite `Content-Type` nem corpo. */
  readonly body?: unknown;
  /**
   * O schema de `@farmacia/contracts` que valida a resposta. `null` para `204 No Content`.
   *
   * Validar a resposta no cliente e barato e pega **deriva de contrato** cedo: a API valida a
   * saida "amostrada a 1% em producao" (`API §10.2`), e 1% de amostra num endpoint pouco usado
   * pode levar semanas para acusar um campo que sumiu.
   *
   * O terceiro parametro do `ZodType` e `unknown` de proposito, e nao e detalhe. Sem ele, a
   * inferencia de `TResponse` casa com o tipo de **entrada** do schema, e os campos de dinheiro
   * chegam a tela como `number` cru em vez de `Cents` — o `CentsSchema` e um `.transform()`,
   * entao entrada e saida sao tipos diferentes de proposito (`CONTRACTS §7.1`). Com `unknown`
   * na entrada, so a saida pode casar.
   */
  readonly response: z.ZodType<TResponse, z.ZodTypeDef, unknown> | null;
  /**
   * A rota exige `Idempotency-Key` (tabela do `API §3.1`)? O escopo default e
   * `METODO caminho`; passe uma string para amarrar a chave a uma intencao mais fina.
   */
  readonly idempotency?: boolean | string;
  readonly ifMatch?: string | null;
  readonly ifNoneMatch?: string | null;
  readonly signal?: AbortSignal;
}

export interface ApiResponse<TResponse> {
  readonly data: TResponse;
  readonly status: number;
  /** O `ETag` da resposta, quando ha. Quem versiona recurso guarda isto. */
  readonly etag: string | null;
  /** O `X-Request-Id` que o servidor ecoou — a chave para achar a requisicao no log. */
  readonly requestId: string | null;
  /** `Idempotent-Replayed: true`: esta resposta e a gravada, o efeito nao aconteceu de novo. */
  readonly replayed: boolean;
  readonly rateLimit: RateLimitSnapshot | null;
}

/**
 * Monta a URL com a query ja normalizada.
 *
 * Duas decisoes:
 * - **lista vira `a,b,c`**, que e a forma que os schemas de `contracts` leem
 *   (`AvailabilityQuerySchema`: *"Lista separada por virgula"*);
 * - **parametros em ordem alfabetica**, espelhando a normalizacao que o `API §1.6` obriga o
 *   servidor a fazer antes de emitir `Content-Location`. Sem isso, `?a=1&b=2` e `?b=2&a=1`
 *   viram duas entradas de cache com o mesmo conteudo — na borda e no service worker.
 */
export function buildUrl(
  baseUrl: string,
  path: string,
  query: Readonly<Record<string, QueryValue>> | undefined,
): string {
  if (query === undefined) return `${baseUrl}${path}`;

  const params = new URLSearchParams();
  for (const name of Object.keys(query).sort()) {
    const value = query[name];
    if (value === undefined) continue;
    params.set(name, Array.isArray(value) ? value.join(',') : String(value));
  }

  const serialized = params.toString();
  return serialized === '' ? `${baseUrl}${path}` : `${baseUrl}${path}?${serialized}`;
}

export interface BuildHeadersInput {
  readonly config: ResolvedApiClientConfig;
  readonly method: HttpMethod;
  readonly hasBody: boolean;
  readonly requestId: string;
  readonly idempotencyKey: string | null;
  readonly ifMatch: string | null;
  readonly ifNoneMatch: string | null;
}

/**
 * O conjunto **fechado** de cabecalhos de saida.
 *
 * Exportado para que o teste possa verificar a lista inteira sem passar por rede — e para que
 * "nenhum `Authorization` e enviado" seja uma afirmacao sobre uma funcao pura, e nao sobre a
 * ausencia de uma linha em algum lugar.
 */
export function buildHeaders(input: BuildHeadersInput): Headers {
  const headers = new Headers();

  headers.set(CLIENT_HEADER_NAME, input.config.clientHeaderValue);
  headers.set(ACCEPT_LANGUAGE_HEADER_NAME, input.config.acceptLanguage);
  headers.set('accept', `${JSON_MEDIA_TYPE}, ${PROBLEM_JSON_MEDIA_TYPE}`);
  headers.set(REQUEST_ID_HEADER_NAME, input.requestId);

  if (input.hasBody) headers.set('content-type', JSON_MEDIA_TYPE);

  if (!SAFE_METHODS.has(input.method)) {
    // Double-submit (`API §5.1`): o valor do cookie legivel `__Host-csrf` volta no header.
    // Ausente, a mutacao segue e o servidor responde `403 FORBIDDEN` — que e a resposta certa
    // e observavel, ao contrario de um erro local que a tela teria de traduzir sozinha.
    const csrf = input.config.readCsrfToken();
    if (csrf !== null && csrf !== '') headers.set(CSRF_HEADER_NAME, csrf);
  }

  if (input.idempotencyKey !== null) {
    headers.set(IDEMPOTENCY_KEY_HEADER_NAME, input.idempotencyKey);
  }
  if (input.ifMatch !== null && input.ifMatch !== '') {
    headers.set(IF_MATCH_HEADER_NAME, input.ifMatch);
  }
  if (input.ifNoneMatch !== null && input.ifNoneMatch !== '') {
    headers.set(IF_NONE_MATCH_HEADER_NAME, input.ifNoneMatch);
  }

  return headers;
}

/** O corpo, quando e JSON legivel. Nunca lanca: corpo ilegivel e informacao, nao excecao. */
async function readJsonBody(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (text === '') return null;
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

/** O motor. `client.ts` o embrulha; nenhum modulo de tela o chama direto. */
export async function performRequest<TResponse>(
  config: ResolvedApiClientConfig,
  keeper: IdempotencyKeeper,
  spec: ApiRequestSpec<TResponse>,
): Promise<ApiResponse<TResponse>> {
  const url = buildUrl(config.baseUrl, spec.path, spec.query);
  const hasBody = spec.body !== undefined;

  const idempotencyScope =
    spec.idempotency === undefined || spec.idempotency === false
      ? null
      : spec.idempotency === true
        ? defaultIdempotencyScope(spec.method, spec.path)
        : spec.idempotency;

  // A chave e resolvida UMA vez, fora do laco. E o que faz "retentativa reusa a chave" ser
  // verdade por construcao, e nao por disciplina de quem escreve o laco.
  const idempotencyKey = idempotencyScope === null ? null : keeper.keyFor(idempotencyScope);

  const ifMatch = spec.ifMatch ?? null;
  const budget = retryBudget({
    method: spec.method,
    path: spec.path,
    hasIdempotencyKey: idempotencyKey !== null,
    hasIfMatch: ifMatch !== null,
  });

  const requestInit: RequestInit = {
    method: spec.method,
    // A LINHA. Sem ela o navegador nao anexa o `__Host-session` e toda rota autenticada
    // responde `401` — a sessao e opaca em cookie, nao ha token para compensar (ADR-0009).
    credentials: 'include',
    ...(hasBody ? { body: JSON.stringify(spec.body) } : {}),
    ...(spec.signal !== undefined ? { signal: spec.signal } : {}),
  };

  for (let attempt = 0; ; attempt += 1) {
    const requestId = config.generateRequestId();
    const headers = buildHeaders({
      config,
      method: spec.method,
      hasBody,
      requestId,
      idempotencyKey,
      ifMatch,
      ifNoneMatch: spec.ifNoneMatch ?? null,
    });

    let response: Response;
    try {
      response = await config.fetch(url, { ...requestInit, headers });
    } catch (cause) {
      // Rede caida nao repete: sem conectividade, repetir so gasta bateria. Quem repete e a
      // aplicacao, no evento `online` (`PWA §8.4`), e ai a chave de idempotencia ainda vale.
      throw new ApiTransportError({
        failure: isAbortError(cause) ? 'ABORTADO' : 'OFFLINE',
        status: null,
        method: spec.method,
        path: spec.path,
        cause,
      });
    }

    const rateLimit = parseRateLimit(response.headers.get(RATE_LIMIT_HEADER_NAME));
    const etag = response.headers.get(ETAG_HEADER_NAME);
    const echoedRequestId = response.headers.get(REQUEST_ID_HEADER_NAME);
    const replayed = response.headers.get(IDEMPOTENT_REPLAYED_HEADER_NAME) === 'true';

    if (response.ok || response.status === 304) {
      if (idempotencyScope !== null) keeper.release(idempotencyScope);

      const data = await decodeSuccess(spec, response);
      return { data, status: response.status, etag, requestId: echoedRequestId, replayed, rateLimit };
    }

    const problem = parseProblemDetails(await readJsonBody(response));
    if (problem === null) {
      // A borda respondeu (HTML de erro, `502` do proxy, corpo truncado). Nao ha codigo de
      // catalogo a inventar — ver `ApiTransportError`.
      throw new ApiTransportError({
        failure: 'RESPOSTA_NAO_CONFORME',
        status: response.status,
        method: spec.method,
        path: spec.path,
      });
    }

    const error = buildApiError({
      problem,
      status: response.status,
      method: spec.method,
      path: spec.path,
    });

    if (SESSION_LOST_CODES.has(error.code)) {
      config.onSessionLost({ code: error.code, status: error.status });
    }

    const retryAfterSeconds = parseRetryAfterSeconds(
      response.headers.get(RETRY_AFTER_HEADER_NAME),
      Date.now(),
    );

    const willRetry = shouldRetry({
      status: response.status,
      action: error.action,
      attempt,
      budget,
    });

    if (response.status === 429) {
      // `API §9`, item 5: telemetria a CADA 429, com a rota — limite mal calibrado tem de
      // aparecer no painel, nao no suporte.
      config.onRateLimited({
        method: spec.method,
        path: spec.path,
        retryAfterSeconds,
        willRetry,
      });
    }

    if (!willRetry) {
      // Resultado determinístico (`API §3.2`): a intencao acabou, a proxima ganha chave nova.
      // Em `5xx` e em falha de rede a chave FICA — e reapresenta-la que impede o efeito duplo.
      if (idempotencyScope !== null && response.status < 500) keeper.release(idempotencyScope);
      throw error;
    }

    await config.sleep(
      jitteredDelayMs(retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS, config.random),
    );
  }
}

/**
 * `409 CART_VERSION_CONFLICT` ganha o tipo que carrega `meta.current` (`API §4.2`).
 *
 * O `meta` e validado com `CartVersionConflictMetaSchema` — e nao lido com um `as`. `meta` vem
 * da rede como `Record<string, unknown>`; um `as CartResponse` ali seria uma afirmacao sobre
 * dado que ninguem conferiu, no exato ponto em que a tipagem para de valer.
 */
function buildApiError(input: ApiErrorInput): ApiError {
  if (input.problem.code === 'CART_VERSION_CONFLICT') {
    const parsed = CartVersionConflictMetaSchema.safeParse(input.problem.meta ?? {});
    return new CartVersionConflictError(input, parsed.success ? parsed.data.current : null);
  }
  return new ApiError(input);
}

function shouldRetry(input: {
  readonly status: number;
  readonly action: string;
  readonly attempt: number;
  readonly budget: number;
}): boolean {
  if (input.attempt >= input.budget) return false;
  if (input.status === 429) return true;
  // A coluna "Acao no cliente" do `API §2.2` e quem decide: so `RETRY` repete.
  if (input.action !== 'RETRY') return false;
  if (input.status >= 500) return input.attempt < RETRY_ATTEMPTS_SERVER_ERROR;
  // `409 IDEMPOTENCY_IN_PROGRESS` e o caso real: o servidor pediu para voltar em `Retry-After`.
  return true;
}

async function decodeSuccess<TResponse>(
  spec: ApiRequestSpec<TResponse>,
  response: Response,
): Promise<TResponse> {
  if (spec.response === null) return undefined as TResponse;
  if (response.status === 204 || response.status === 304) return undefined as TResponse;

  const body = await readJsonBody(response);
  const parsed = spec.response.safeParse(body);
  if (!parsed.success) {
    // Deriva de contrato: o corpo chegou, mas nao e o que `@farmacia/contracts` descreve.
    // Falhar aqui e melhor que entregar um objeto meio-preenchido para a tela renderizar.
    throw new ApiTransportError({
      failure: 'RESPOSTA_NAO_CONFORME',
      status: response.status,
      method: spec.method,
      path: spec.path,
      cause: parsed.error,
    });
  }
  return parsed.data;
}
